/**
 * `/settings/webhooks/new` — create-endpoint slide-over.
 *
 * Renders as a right-side sheet over the index list. Submits to
 * `POST /v1/webhooks/endpoints` via `useCreateEndpoint()` and, on
 * success, hands the one-time `signing_secret` to
 * `<SigningSecretReveal>`. Closing the reveal returns the user to
 * the list.
 *
 * Form contract (mirrors the phase 2 `CreateEndpointSchema`):
 *   - `url`         required, HTTPS, validated server-side. For
 *                   `kind: 'slack'`  → must match `hooks.slack.com/services/...`.
 *                   For `kind: 'discord'` → must match
 *                   `discord.com/api/webhooks/...`. We surface server
 *                   errors via `FetchError` on submit; no client-side
 *                   regex duplication.
 *   - `kind`        generic | slack | discord (segmented).
 *   - `description` optional, max 512.
 *   - `event_types` empty = all. Checkbox per catalogue entry.
 *   - `headers`     up to 8 key/value pairs. Reserved headers
 *                   (`host`, `content-type`, etc.) rejected server-side.
 */
import { SigningSecretReveal } from '@/components/primitives/SigningSecretReveal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { FetchError } from '@/lib/api';
import { toastFromError, toastSuccess } from '@/lib/errors';
import type {
  CreateEndpointBody,
  WebhookEndpointKind,
  WebhookEndpointWithSecret,
} from '@/lib/queries';
import { useCreateEndpoint } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { Plus, Trash } from '@phosphor-icons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/webhooks/new')({
  component: NewWebhookSlideOver,
});

// Keep this list local-sourced from @clickweave/shared in a future
// follow-up. For now the dashboard duplicates the catalogue to
// avoid pulling a Node-ish workspace into the client bundle.
//
// The order here is the order shown to the user — most-common events
// first, then the lower-volume / advanced ones. `monitor.regression.detected`
// is intentionally absent because no producer emits it yet (Phase 5 work).
const EVENT_CATALOGUE: { id: string; label: string; hint: string }[] = [
  {
    id: 'audit.completed',
    label: 'audit.completed',
    hint: 'Fires when a snapshot finishes successfully. Carries scores + metrics.',
  },
  {
    id: 'audit.failed',
    label: 'audit.failed',
    hint: 'Fires when a snapshot errors out or times out.',
  },
  {
    id: 'audit.started',
    label: 'audit.started',
    hint: 'Fires when a snapshot starts running. High volume — opt in.',
  },
  {
    id: 'monitor.run.failed',
    label: 'monitor.run.failed',
    hint: 'Fires when a monitor run ends with one or more failed snapshots.',
  },
  {
    id: 'usage.threshold.warning',
    label: 'usage.threshold.warning',
    hint: 'Fires when monthly usage on any plan axis crosses 80% of the included quota.',
  },
  {
    id: 'usage.threshold.critical',
    label: 'usage.threshold.critical',
    hint: 'Fires when monthly usage on any plan axis crosses 90% of the included quota.',
  },
];

const KINDS: { value: WebhookEndpointKind; label: string }[] = [
  { value: 'generic', label: 'Generic' },
  { value: 'slack', label: 'Slack' },
  { value: 'discord', label: 'Discord' },
];

const MAX_HEADERS = 8;

interface HeaderRow {
  key: string;
  value: string;
}

function NewWebhookSlideOver(): ReactElement {
  const navigate = useNavigate();
  const mutation = useCreateEndpoint();

  const [open, setOpen] = useState(true);
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState<WebhookEndpointKind>('generic');
  const [description, setDescription] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  // Per-endpoint signing toggle. Defaults OFF — Slack/Discord and most
  // generic receivers don't verify signatures (the URL itself is the
  // auth). Customers who want HMAC verification opt in here. The
  // signing secret reveal sheet only shows when the toggle is on at
  // submit time; secrets are still generated server-side either way
  // so flipping it on later doesn't require rotation.
  const [signingEnabled, setSigningEnabled] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [revealed, setRevealed] = useState<WebhookEndpointWithSecret | null>(null);

  function toggleEvent(id: string): void {
    setEventTypes((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  }

  function addHeader(): void {
    if (headers.length >= MAX_HEADERS) return;
    setHeaders((prev) => [...prev, { key: '', value: '' }]);
  }

  function updateHeader(idx: number, patch: Partial<HeaderRow>): void {
    setHeaders((prev) => prev.map((h, i) => (i === idx ? { ...h, ...patch } : h)));
  }

  function removeHeader(idx: number): void {
    setHeaders((prev) => prev.filter((_, i) => i !== idx));
  }

  function onSheetOpenChange(next: boolean): void {
    setOpen(next);
    if (!next && !revealed) {
      // User cancelled before creating — drop back to the list.
      navigate({ to: '/settings/webhooks' });
    }
  }

  function onRevealClose(): void {
    setRevealed(null);
    navigate({ to: '/settings/webhooks' });
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(undefined);

    if (!url.trim()) {
      setFormError('URL is required.');
      return;
    }

    const headersRecord: Record<string, string> = {};
    for (const row of headers) {
      const k = row.key.trim();
      const v = row.value.trim();
      if (!k && !v) continue;
      if (!k) {
        setFormError('Header name is required when a value is set.');
        return;
      }
      headersRecord[k] = v;
    }

    const body: CreateEndpointBody = {
      url: url.trim(),
      kind,
      event_types: eventTypes,
      signing_enabled: signingEnabled,
    };
    const trimmedDescription = description.trim();
    if (trimmedDescription) body.description = trimmedDescription;
    if (Object.keys(headersRecord).length > 0) body.headers = headersRecord;

    try {
      const response = await mutation.mutateAsync(body);
      toastSuccess('Endpoint created', `${response.url} is ready to receive events.`);
      setOpen(false);
      // Only show the secret-reveal sheet when signing is on. With
      // signing off the secret is meaningless — and showing it would
      // imply we're sending it on the wire, which we aren't.
      if (signingEnabled) {
        setRevealed(response);
      } else {
        navigate({ to: '/settings/webhooks' });
      }
    } catch (err) {
      if (err instanceof FetchError) setFormError(err.message);
      else toastFromError(err);
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onSheetOpenChange}>
        <SheetPopup className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Create webhook endpoint</SheetTitle>
            <SheetDescription>
              Pick a destination type, then paste the URL. The signing secret is shown once after
              you save.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} noValidate className="contents">
            <SheetPanel>
              <div className="space-y-5 p-6">
                <Field>
                  <FieldLabel>Destination</FieldLabel>
                  <KindSegmented value={kind} onChange={setKind} />
                </Field>

                <Field>
                  <FieldLabel htmlFor="webhook-url">URL</FieldLabel>
                  <Input
                    id="webhook-url"
                    type="url"
                    required
                    placeholder={
                      kind === 'slack'
                        ? 'https://hooks.slack.com/services/…'
                        : kind === 'discord'
                          ? 'https://discord.com/api/webhooks/…'
                          : 'https://example.com/clickweave'
                    }
                    value={url}
                    onChange={(e) => setUrl((e.target as HTMLInputElement).value)}
                    autoFocus
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="webhook-description">Description</FieldLabel>
                  <Input
                    id="webhook-description"
                    placeholder="Production alerts"
                    value={description}
                    onChange={(e) => setDescription((e.target as HTMLInputElement).value)}
                  />
                </Field>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--color-ink)]">Events</div>
                  <p className="text-xs text-[var(--color-mute)]">
                    Leave every box unchecked to receive all events. Tick specific events to
                    subscribe only to those.
                  </p>
                  <ul className="space-y-2">
                    {EVENT_CATALOGUE.map((e) => {
                      const checked = eventTypes.includes(e.id);
                      return (
                        <li
                          key={e.id}
                          className="flex items-start gap-3 rounded-lg border border-[var(--color-line)] p-3"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleEvent(e.id)}
                            aria-label={e.label}
                          />
                          <div className="flex-1">
                            <div className="font-mono text-xs text-[var(--color-ink)]">
                              {e.label}
                            </div>
                            <div className="text-xs text-[var(--color-mute)]">{e.hint}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-[var(--color-ink)]">
                      Static headers
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={addHeader}
                      disabled={headers.length >= MAX_HEADERS}
                    >
                      <Plus aria-hidden="true" />
                      Add header
                    </Button>
                  </div>
                  <p className="text-xs text-[var(--color-mute)]">
                    Up to {MAX_HEADERS} custom headers are added to every delivery. Reserved headers
                    (<span className="font-mono">content-type</span>,{' '}
                    <span className="font-mono">X-Clickweave-Signature</span>, …) are rejected.
                  </p>
                  {headers.length > 0 && (
                    <div className="space-y-2">
                      {headers.map((h, idx) => (
                        <div
                          key={`header-${
                            // biome-ignore lint/suspicious/noArrayIndexKey: header rows keyed by position; reorder is not supported
                            idx
                          }`}
                          className="flex items-center gap-2"
                        >
                          <Input
                            placeholder="X-Authorization"
                            value={h.key}
                            onChange={(e) =>
                              updateHeader(idx, { key: (e.target as HTMLInputElement).value })
                            }
                          />
                          <Input
                            placeholder="Bearer …"
                            value={h.value}
                            onChange={(e) =>
                              updateHeader(idx, { value: (e.target as HTMLInputElement).value })
                            }
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            aria-label="Remove header"
                            onClick={() => removeHeader(idx)}
                          >
                            <Trash aria-hidden="true" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Per-endpoint HMAC signing toggle. Defaults OFF.
                    Slack/Discord don't verify; generic receivers
                    usually authenticate via the URL or static headers.
                    Customers who want HMAC verification opt in here.
                    The signing secret is generated server-side
                    regardless so flipping the toggle on later doesn't
                    require rotation. */}
                <div className="space-y-2 rounded-lg border border-[var(--color-line)] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-[var(--color-ink)]">
                        Sign requests with HMAC-SHA256
                      </div>
                      <p className="text-xs text-[var(--color-mute)]">
                        When on, every delivery includes an{' '}
                        <span className="font-mono">X-Clickweave-Signature</span> header your
                        receiver can verify with the signing secret. Slack and Discord webhooks
                        don't verify signatures — leave this off for those.
                      </p>
                    </div>
                    <Switch
                      checked={signingEnabled}
                      onCheckedChange={setSigningEnabled}
                      aria-label="Sign requests with HMAC-SHA256"
                    />
                  </div>
                </div>

                {formError && <FieldError>{formError}</FieldError>}
              </div>
            </SheetPanel>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => onSheetOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                Create endpoint
              </Button>
            </SheetFooter>
          </form>
        </SheetPopup>
      </Sheet>

      <SigningSecretReveal
        secret={revealed?.signing_secret ?? null}
        onClose={onRevealClose}
        title="Endpoint ready"
        description="Copy the signing secret and deploy it to your receiver. You can look it up again later from the endpoint detail page — but it's easiest to grab it now before the form resets."
        codeLabel="Signing secret"
        meta={
          revealed && (
            <>
              Verify every delivery by computing{' '}
              <span className="font-mono">
                HMAC-SHA256(secret, `${'{t}'}.${'{body}'}`)
              </span>{' '}
              and comparing to <span className="font-mono">v1=…</span> in the{' '}
              <span className="font-mono">X-Clickweave-Signature</span> header.
            </>
          )
        }
      />
    </>
  );
}

function KindSegmented({
  value,
  onChange,
}: {
  value: WebhookEndpointKind;
  onChange: (next: WebhookEndpointKind) => void;
}): ReactElement {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: segmented control uses div+role="group" to match existing dashboard pattern
      role="group"
      aria-label="Destination kind"
      className="inline-flex items-center overflow-hidden rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] text-sm"
    >
      {KINDS.map((k) => {
        const active = k.value === value;
        return (
          <button
            key={k.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(k.value)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 outline-none transition-colors',
              active
                ? 'bg-[var(--color-paper-alt)] font-medium text-[var(--color-ink)]'
                : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-alt)]/60',
            )}
          >
            {k.label}
          </button>
        );
      })}
    </div>
  );
}
